import type { FormEvent, MouseEvent } from 'react';
import type { Location } from 'history';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import SwankySwatchBox from 'components/productdetail/swankyswatch/SwankySwatchBox';
import { cn } from 'helpers/classnames';
import { evProductDimensionSelected } from 'events/product';
import { trackError } from 'helpers/ErrorUtils';
import ProductUtils, { getAfterPayProps } from 'helpers/ProductUtils';
import AddToCart from 'components/productdetail/stylepicker/AddToCart';
import ColorChooser from 'components/productdetail/stylepicker/ColorChooser';
import SizingChooser from 'components/productdetail/stylepicker/SizingChooser';
import TwoDayShippingPerk from 'components/productdetail/stylepicker/TwoDayShippingPerk';
import PageContent from 'components/landing/PageContent';
import marketplace from 'cfg/marketplace.json';
import { track } from 'apis/amethyst';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import type { MapSomeDimensionIdTo, ProductStyle, SizingValue } from 'types/cloudCatalog';
import type { FormattedProductSizing, ProductDetailState, StyleThumbnail } from 'reducers/detail/productDetail';
import type { SelectedSizing } from 'types/product';
import { stockSelectionCompleted } from 'store/ducks/productDetail/actions';
import AfterPay from 'components/productdetail/AfterPay';
import { FEATURE_PDP_OOS_UX } from 'constants/features';
import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';

import css from 'styles/components/productdetail/stylePicker.scss';

const {
  pdp: { addToCartText, lowStockMessage, sizingPlaceholder, showStyleChooserPrefix, hideTwoDayShippingPerk }
} = marketplace;

interface DefaultProps {
  makeProductNotifyMe?: () => JSX.Element;
  showOosNotifyMe: boolean;
  styleList: ProductStyle[];
  product: ProductDetailState;
  productId: string;
  productType: string;
  productImage: string;
  productTitle?: string;
  sizing: FormattedProductSizing;
  genders: string[];
  selectedSizing: SelectedSizing;
  selectedStyle: ProductStyle;
  thumbnails: StyleThumbnail[];
  dimensionValidation: Partial<MapSomeDimensionIdTo<boolean>>;
  onStockChange: (
    styleId: string,
    selectedSizing: SelectedSizing,
    {
      label,
      name,
      selectedOption
    }: {
      label: string | null;
      name?: string;
      selectedOption?: SizingValue;
    }
  ) => void;
  onStyleChange?: (e: MouseEvent<HTMLButtonElement>) => void;
  onAddToCart: (e: MouseEvent<HTMLButtonElement> | FormEvent<HTMLFormElement>) => void;
  addToCartAction: string;
  isGiftCard: boolean;
  isSelectSizeTooltipVisible: boolean | undefined;
  isSelectSizeTooltipHighlighted: boolean | undefined;
  onShowSelectSizeTooltip: () => void;
  onHideSelectSizeTooltip: () => void;
  onUnhighlightSelectSizeTooltip: () => void;
  onValidationClick?: () => void;
  showSizeGender: boolean;
  allowTwoDayShippingPrimePerk: boolean;
  showSizeChartLink: boolean;
  sizingPredictionId?: string | null;
  isOnDemandEligible?: boolean | null | undefined;
  hasRecommendedSizing: boolean;
  location: Location;
  sizeSymphonyContent?: unknown;
  addToCartSymphonyContent?: unknown;
  pageType: string;
  includeColorDropDown?: boolean;
  id?: string;
  showAddToCart?: boolean;
  useDropdowns: boolean;
  useStickyAddToCart: boolean;
  isPreview?: boolean;
  productSizeChanged?: Function;
  hidePerks?: boolean;
  showSymphonySizeBuyBox?: boolean;
  shouldExcludeNotifyMe?: boolean;
}

type AirplaneSeatSizingActionProps = {
  productAgeGroupChanged?: Function;
  productGenderChanged?: Function;
  productSingleShoeSideChanged?: Function;
  productSizeRangeChanged?: Function;
  productSizeUnitChanged?: Function;
  validateDimensions?: Function;
  removeGenderFilter?: boolean;
};

type StylePickerProps = DefaultProps & PropsFromRedux & Partial<AirplaneSeatSizingActionProps>;

export const StylePicker = (props: StylePickerProps) => {
  const { isEnrolled: isPdpOosUx } = useIsEnrolledInVariant({ featureFlag: FEATURE_PDP_OOS_UX, isZapposOnly: false });
  const trackColorChangeEvent = (styleId: string, colorName: string, isNew: string | undefined) => {
    const { pageType } = props;
    const eventData = {
      dimension: 'COLOR_DIMENSION',
      dimensionId: styleId,
      dimensionLabel: colorName,
      sourcePage: pageType,
      isNew: isNew === 'true'
    };
    track(() => [evProductDimensionSelected, eventData]);
  };

  const onColorChange = (styleId: string, colorName: string | undefined, isNew: string | undefined) => {
    const { onStockChange, selectedSizing } = props;
    onStockChange(styleId, selectedSizing, { label: 'color' });
    trackColorChangeEvent(styleId, colorName!, isNew);
  };

  const onSizeChange = (
    options: SizingValue[],
    event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement> | React.MouseEvent<HTMLInputElement>
  ) => {
    const { onStockChange, pageType, productId, selectedSizing, selectedStyle, sizing } = props;
    const newSizes = Object.assign({}, selectedSizing, {});
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    const trackLabel = target.getAttribute('data-track-label');
    newSizes[name] = value;
    const selectedOption = options.find(option => option.id === value);
    onStockChange(selectedStyle.styleId, newSizes, {
      label: trackLabel,
      name,
      selectedOption
    });
    if (selectedOption) {
      ProductUtils.generateProductDimensionSelectedAmethystEvent(
        sizing.dimensionIdToName[name],
        selectedOption.value,
        pageType,
        options,
        selectedSizing,
        name,
        value,
        sizing,
        selectedStyle.colorId,
        selectedStyle
      );
    } else if (value) {
      trackError(`Unable to find matching option for size id ${value} on productId ${productId}`, 'ERROR');
    }
  };

  const makeColorChooser = (showStyleChooserPrefix: boolean) => {
    const { isGiftCard, product, productType, selectedStyle, styleList, useDropdowns, pageType } = props;

    const { detail: { sizing: { airplaneCache = undefined } = {} } = {} } = product;

    if (!useDropdowns) {
      const initialMode = props.styleList.length <= 9 ? 'grid' : 'carousel';

      return (
        <SwankySwatchBox
          initialMode={initialMode}
          isGiftCard={isGiftCard}
          onColorChange={onColorChange}
          productState={product}
          airplaneCache={airplaneCache}
          productType={productType}
          selectedStyle={selectedStyle}
          styleList={styleList}
          pageType={pageType}
        />
      );
    }

    return (
      <ColorChooser
        isGiftCard={isGiftCard}
        onColorChange={onColorChange}
        selectedStyle={selectedStyle}
        showColorLabel={true}
        showStyleChooserPrefix={showStyleChooserPrefix}
        styleList={styleList}
      />
    );
  };

  const {
    makeProductNotifyMe,
    onAddToCart,
    addToCartAction,
    selectedStyle,
    showOosNotifyMe,
    sizing,
    selectedSizing,
    product,
    productType,
    genders,
    dimensionValidation,
    onShowSelectSizeTooltip,
    onHideSelectSizeTooltip,
    onValidationClick,
    includeColorDropDown,
    isGiftCard,
    isSelectSizeTooltipVisible,
    isSelectSizeTooltipHighlighted,
    showSizeGender,
    showSizeChartLink,
    sizingPredictionId,
    isOnDemandEligible,
    hasRecommendedSizing,
    location,
    sizeSymphonyContent,
    addToCartSymphonyContent,
    id,
    showAddToCart = true,
    useDropdowns,
    useStickyAddToCart,
    isPreview = false,
    productSizeChanged,
    productAgeGroupChanged,
    productGenderChanged,
    productSingleShoeSideChanged,
    productSizeRangeChanged,
    productSizeUnitChanged,
    validateDimensions,
    styleList,
    hidePerks,
    showSymphonySizeBuyBox,
    removeGenderFilter,
    shouldExcludeNotifyMe
  } = props;

  const airplaneProps = {
    productAgeGroupChanged,
    productGenderChanged,
    productSingleShoeSideChanged,
    productSizeRangeChanged,
    productSizeUnitChanged,
    validateDimensions
  };

  const { productId, finalSale, styleId, price, originalPrice } = selectedStyle || {};
  const { priceDollarValue, priceCentsValue, isProductAfterpayEligible } = getAfterPayProps({ price, originalPrice, productType });

  const showProductNotifyMe = showOosNotifyMe && !isGiftCard;

  const sizingChooser = (
    <>
      <SizingChooser
        id={id}
        product={product}
        productId={productId}
        productType={productType}
        genders={genders}
        sizing={sizing}
        selectedSizing={selectedSizing}
        dimensionValidation={dimensionValidation}
        sizingPlaceholder={sizingPlaceholder}
        onSizeChange={onSizeChange}
        showSizeGender={showSizeGender}
        showSizeChartLink={showSizeChartLink}
        isSelectSizeTooltipVisible={isSelectSizeTooltipVisible}
        isSelectSizeTooltipHighlighted={isSelectSizeTooltipHighlighted}
        sizingPredictionId={sizingPredictionId}
        isOnDemandEligible={isOnDemandEligible}
        hasRecommendedSizing={hasRecommendedSizing}
        location={location}
        sizeSymphonyContent={sizeSymphonyContent}
        useDropdowns={useDropdowns}
        productSizeChanged={productSizeChanged}
        styleId={styleId}
        makeProductNotifyMe={makeProductNotifyMe}
        showProductNotifyMe={showProductNotifyMe}
        colorId={selectedStyle.colorId}
        styleList={styleList}
        showSymphonySizeBuyBox={showSymphonySizeBuyBox}
        removeGenderFilter={removeGenderFilter}
        {...airplaneProps}
      />
    </>
  );

  return (
    <form method="POST" id="buyBoxForm" action={addToCartAction} onSubmit={onAddToCart} className="flex flex-col gap-y-6">
      {includeColorDropDown && makeColorChooser(showStyleChooserPrefix)}
      {!isGiftCard && sizingChooser}
      {!!addToCartSymphonyContent && (
        <PageContent containerDataId="pdp-buybox" additionalClassName={css.symphonyBuyBoxPageContentCart} slotDetails={addToCartSymphonyContent} />
      )}
      {showAddToCart && (
        <div className={cn(css.atcContainer, { [css.disableClick]: isPreview }, isPdpOosUx ? 'gap-y-[10px]' : 'gap-y-6')}>
          <div className={css.atcWrapper}>
            <AddToCart
              onAddToCart={onAddToCart}
              productStyle={selectedStyle}
              selectedSizing={selectedSizing}
              sizing={sizing}
              addToCartText={addToCartText}
              lowStockMessage={lowStockMessage}
              useStickyAddToCart={useStickyAddToCart}
              onShowSelectSizeTooltip={onShowSelectSizeTooltip}
              onHideSelectSizeTooltip={onHideSelectSizeTooltip}
              onValidationClick={onValidationClick}
              isGiftCard={isGiftCard}
              productDetail={product.detail}
              shouldExcludeNotifyMe={shouldExcludeNotifyMe}
              onStyleChange={props.onStyleChange}
            />
          </div>
          {(priceDollarValue || (!hidePerks && !finalSale && hideTwoDayShippingPerk)) && (
            <div className="flex flex-col gap-y-3">
              {priceDollarValue && (
                <>
                  <AfterPay
                    isProductAfterpayEligible={isProductAfterpayEligible}
                    priceDollarValue={priceDollarValue}
                    priceCentsValue={priceCentsValue}
                  />
                </>
              )}
              {!hidePerks && !finalSale && hideTwoDayShippingPerk && <TwoDayShippingPerk />}
            </div>
          )}
        </div>
      )}
    </form>
  );
};

StylePicker.defaultProps = {
  includeColorDropDown: true
};

const connector = connect(null, { stockSelectionCompleted });
type PropsFromRedux = ConnectedProps<typeof connector>;
export default withErrorBoundary('StylePicker', connector(StylePicker));
